import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, PageHeader, Stepper, StepperNavFooter } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ArrowLeft } from 'lucide-react';

import { USER_TYPE } from '../../constants/copy.js';
import { INVITE_STEP } from '../../constants/inviteRules.js';
import {
  useInviteMember,
  useInviteMembers,
  useInviteOptions,
  usePeopleAccessPaths,
} from '../../hooks';
import GroupsStep from './GroupsStep';
import PeopleStep from './PeopleStep';
import ReviewStep from './ReviewStep';
import RolesStep from './RolesStep';
import FullPageFrame from '../shared/FullPageFrame';

/**
 * The full-page invite builder: Stepper on top, the current step in the middle and the
 * StepperNavFooter at the bottom. Which step comes next and when Next is allowed is decided by
 * `inviteSteps` (through the provider); this component only wires them to the UI.
 */
function InviteMemberBuilder() {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const {
    state,
    steps,
    nextStep,
    previousStep,
    isLastStep,
    gate,
    recipientCount,
    goToStep,
    commitDraft,
  } = useInviteMember();
  const {
    roles,
    groups,
    studios,
    loading: optionsLoading,
    error: optionsError,
  } = useInviteOptions();

  const { membersPath } = usePeopleAccessPaths();
  const goToMembers = useCallback(() => navigate(membersPath), [navigate, membersPath]);
  const { submit, submitting, error, resetError } = useInviteMembers({ onSuccess: goToMembers });

  // A failed send is only relevant on Review; leaving it clears the notice.
  useEffect(() => {
    if (state.currentStep !== INVITE_STEP.REVIEW) resetError();
  }, [state.currentStep, resetError]);

  const stepItems = useMemo(() => {
    const currentIndex = steps.indexOf(state.currentStep);
    return steps.map((id, index) => ({
      id,
      label: t(`invite.steps.${id}`),
      state: index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'upcoming',
    }));
  }, [steps, state.currentStep, t]);

  const handlePrimary = useCallback(async () => {
    if (isLastStep) {
      const roleNames = roles.filter((r) => state.roleIds.includes(r.id)).map((r) => r.name);
      submit(state, { roleNames: state.userType === USER_TYPE.MEMBER ? roleNames : [] });
      return;
    }
    if (state.currentStep === INVITE_STEP.PEOPLE && state.draft.trim()) {
      // Next confirms an address that was typed but not yet entered with a comma or Enter.
      const { added, invalid } = await commitDraft();
      if (invalid > 0 || state.emails.length + added === 0) return;
    }
    goToStep(nextStep);
  }, [isLastStep, roles, submit, state, commitDraft, goToStep, nextStep]);
  const handleBack = useCallback(
    () => (previousStep ? goToStep(previousStep) : goToMembers()),
    [previousStep, goToStep, goToMembers],
  );

  const primaryLabel = !isLastStep
    ? t('invite.actions.next')
    : t(state.sendEmail ? 'invite.actions.sendInvitations' : 'invite.actions.createContacts', {
        count: recipientCount,
      });

  return (
    <div data-testid="page-member-invite" className="flex min-h-full flex-1 flex-col">
      <FullPageFrame>
        <div className="flex flex-col gap-6">
          <div className="flex items-start gap-3">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={ArrowLeft}
              aria-label={t('invite.backToMembers')}
              onClick={goToMembers}
            />
            <PageHeader title={t('invite.title')} subtitle={t('invite.subtitle')} />
          </div>
          <Stepper
            steps={stepItems}
            onStepClick={goToStep}
            ariaLabel={t('invite.stepsLabel')}
          />
          <div className="max-w-3xl">
            {state.currentStep === INVITE_STEP.PEOPLE && <PeopleStep />}
            {state.currentStep === INVITE_STEP.ROLES && (
              <RolesStep roles={roles} loading={optionsLoading} error={Boolean(optionsError)} />
            )}
            {state.currentStep === INVITE_STEP.GROUPS && (
              <GroupsStep groups={groups} loading={optionsLoading} error={Boolean(optionsError)} />
            )}
            {state.currentStep === INVITE_STEP.REVIEW && (
              <ReviewStep
                roles={roles}
                groups={groups}
                studios={studios}
                hasError={Boolean(error)}
              />
            )}
          </div>
        </div>
      </FullPageFrame>
      <StepperNavFooter
        ariaLabel={t('invite.footerLabel')}
        backLabel={previousStep ? t('invite.actions.back') : t('invite.actions.cancel')}
        backVariant={previousStep ? 'back' : 'cancel'}
        onBack={handleBack}
        primaryLabel={primaryLabel}
        onPrimary={handlePrimary}
        primaryDisabled={!gate.ok}
        primaryDisabledReason={gate.ok ? undefined : t(`invite.blocked.${gate.reason}`)}
        primaryLoading={submitting}
        busy={submitting}
      />
    </div>
  );
}

export default InviteMemberBuilder;
